'use client';

import React, { useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CTABanner from '@/components/CTABanner';

interface FormState {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

// Toast component
function Toast({ message, type, onClose }: { message: string; type: 'success' | 'error'; onClose: () => void }) {
  return (
    <div style={{
      position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999,
      background: type === 'success' ? '#00b28a' : '#e53e3e',
      color: '#ffffff', borderRadius: '12px', padding: '14px 20px',
      fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 600,
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '10px',
      maxWidth: '360px', animation: 'slideUp 0.3s ease',
    }}>
      <span>{type === 'success' ? '✓' : '⚠'}</span>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
    </div>
  );
}

export default function ContactPage() {
  const [form, setForm] = useState<FormState>({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Please enter a valid email';
    if (!form.message.trim()) newErrors.message = 'Message is required';
    else if (form.message.trim().length < 10) newErrors.message = 'Message must be at least 10 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1200));
    setSubmitting(false);
    setSubmitted(true);
    showToast('Message sent! We\'ll get back to you within 24 hours.', 'success');
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '12px 16px',
    border: '1.5px solid #e2e8f0', borderRadius: '10px',
    fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', color: '#1a2b4a',
    outline: 'none', transition: 'border-color 0.2s', boxSizing: 'border-box', background: '#fafbfc',
  };

  const contactInfo = [
    { icon: '📧', label: 'Email', value: 'support@lendo.com', href: 'mailto:support@lendo.com' },
    { icon: '📞', label: 'Phone', value: '1-800-LENDO-01', href: 'tel:18005363601' },
    { icon: '🕐', label: 'Hours', value: 'Mon–Fri, 9am–6pm EST', href: null },
    { icon: '📍', label: 'Address', value: '123 Financial District, New York, NY 10004', href: null },
  ];

  return (
    <>
      <Navbar />
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <main style={{ paddingTop: '88px', minHeight: '100vh', background: '#f7f9fc' }}>
        {/* Header */}
        <div style={{ background: '#1a2b4a', padding: '60px 24px' }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <h1 style={{ fontFamily: 'Merriweather, serif', fontWeight: 900, fontSize: '2.2rem', color: '#ffffff', margin: '0 0 12px' }}>
              Contact Us
            </h1>
            <p style={{ fontFamily: 'DM Sans, sans-serif', color: 'rgba(255,255,255,0.75)', fontSize: '1rem', margin: 0, maxWidth: '500px', lineHeight: 1.6 }}>
              Have a question or need help? Our team is here to assist you. We typically respond within 24 hours.
            </p>
          </div>
        </div>

        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '48px 24px 80px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '32px', alignItems: 'flex-start' }}>

            {/* Contact Info */}
            <div>
              <div style={{ background: '#ffffff', borderRadius: '16px', padding: '32px', border: '1px solid #e8edf3', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: '20px' }}>
                <h2 style={{ fontFamily: 'Merriweather, serif', fontWeight: 700, fontSize: '1.2rem', color: '#1a2b4a', margin: '0 0 24px' }}>
                  Get in Touch
                </h2>
                {contactInfo.map((item) => (
                  <div key={item.label} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '20px' }}>
                    <div style={{ width: '40px', height: '40px', background: '#f0faf7', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem', flexShrink: 0 }}>
                      {item.icon}
                    </div>
                    <div>
                      <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.82rem', color: '#718096', margin: '0 0 2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{item.label}</p>
                      {item.href ? (
                        <a href={item.href} style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem', color: '#00b28a', textDecoration: 'none', fontWeight: 600 }}>{item.value}</a>
                      ) : (
                        <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.92rem', color: '#1a2b4a', margin: 0 }}>{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* FAQ link */}
              <div style={{ background: '#f0faf7', borderRadius: '16px', padding: '24px', border: '1px solid #b2e8d8' }}>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '0.95rem', color: '#1a2b4a', margin: '0 0 8px' }}>
                  Looking for quick answers?
                </p>
                <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.88rem', color: '#4a5568', margin: '0 0 16px', lineHeight: 1.6 }}>
                  Check our FAQ page for answers to the most common questions about loans, applications, and our process.
                </p>
                <a href="/faq" style={{ display: 'inline-block', padding: '10px 20px', background: '#00b28a', color: '#ffffff', borderRadius: '8px', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.88rem', textDecoration: 'none' }}>
                  View FAQ →
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div style={{ background: '#ffffff', borderRadius: '16px', padding: '40px', border: '1px solid #e8edf3', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
              {submitted ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div style={{ width: '64px', height: '64px', background: '#f0faf7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '1.8rem' }}>✅</div>
                  <h2 style={{ fontFamily: 'Merriweather, serif', fontWeight: 700, fontSize: '1.4rem', color: '#1a2b4a', margin: '0 0 12px' }}>Message Sent!</h2>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#718096', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 24px' }}>
                    Thank you for reaching out. Our team will get back to you within 24 hours.
                  </p>
                  <button onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                    style={{ padding: '12px 28px', background: '#00b28a', color: '#ffffff', border: 'none', borderRadius: '10px', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer' }}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 style={{ fontFamily: 'Merriweather, serif', fontWeight: 700, fontSize: '1.3rem', color: '#1a2b4a', margin: '0 0 28px' }}>Send us a message</h2>
                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Full Name *</label>
                        <input type="text" value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: undefined }); }}
                          placeholder="Jane Smith"
                          style={{ ...inputStyle, borderColor: errors.name ? '#e53e3e' : '#e2e8f0' }}
                          onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                          onBlur={(e) => (e.currentTarget.style.borderColor = errors.name ? '#e53e3e' : '#e2e8f0')} />
                        {errors.name && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#e53e3e', marginTop: '4px', marginBottom: 0 }}>{errors.name}</p>}
                      </div>
                      <div>
                        <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Email Address *</label>
                        <input type="email" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors({ ...errors, email: undefined }); }}
                          placeholder="you@example.com"
                          style={{ ...inputStyle, borderColor: errors.email ? '#e53e3e' : '#e2e8f0' }}
                          onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                          onBlur={(e) => (e.currentTarget.style.borderColor = errors.email ? '#e53e3e' : '#e2e8f0')} />
                        {errors.email && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#e53e3e', marginTop: '4px', marginBottom: 0 }}>{errors.email}</p>}
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                      <div>
                        <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Phone (optional)</label>
                        <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 000-0000" style={inputStyle}
                          onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                          onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')} />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Subject</label>
                        <select value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} style={inputStyle}
                          onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                          onBlur={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}>
                          <option value="">Select a topic</option>
                          <option>Loan Application</option>
                          <option>Document Upload</option>
                          <option>Account Issues</option>
                          <option>Loan Status</option>
                          <option>Partnership Inquiry</option>
                          <option>Other</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ marginBottom: '24px' }}>
                      <label style={{ display: 'block', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#2d3748', marginBottom: '8px' }}>Message *</label>
                      <textarea value={form.message} onChange={(e) => { setForm({ ...form, message: e.target.value }); if (errors.message) setErrors({ ...errors, message: undefined }); }}
                        placeholder="Tell us how we can help you…" rows={5}
                        style={{ ...inputStyle, resize: 'vertical', minHeight: '120px', borderColor: errors.message ? '#e53e3e' : '#e2e8f0' }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                        onBlur={(e) => (e.currentTarget.style.borderColor = errors.message ? '#e53e3e' : '#e2e8f0')} />
                      {errors.message && <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#e53e3e', marginTop: '4px', marginBottom: 0 }}>{errors.message}</p>}
                    </div>
                    <button type="submit" disabled={submitting}
                      style={{ width: '100%', padding: '14px', background: submitting ? '#a0d4c8' : '#00b28a', color: '#ffffff', border: 'none', borderRadius: '10px', fontFamily: 'DM Sans, sans-serif', fontWeight: 700, fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}
                      onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = '#009970'; }}
                      onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.background = '#00b28a'; }}>
                      {submitting ? 'Sending…' : 'Send Message'}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>

        <CTABanner />
      </main>
      <Footer />
      <style>{`
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @media (max-width: 768px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </>
  );
}
